import Loader from '@/components/Loader';
import Constants from '@/Constants';
import { ImportDetails } from '@/models/Person';
import http from '@/services/HttpService';
import { getAuth } from '@/services/IdentityService';
import { getHost } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { getImportDetailRequest } from '@/store/person/action';
import React, { useEffect, useState } from 'react';
import Pagination from 'react-js-pagination';
import { useDispatch, useSelector } from 'react-redux';
import './style.scss';

interface ModalProps {
    status: string | undefined;
    personId: number | undefined;
    id: number | undefined;
    target: string;
}

const Modal = ({ id, target, status, personId }: ModalProps) => {
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [person, setPerson] = useState({ name: 'None', email: 'None' });
    const [personLoading, setPersonLoading] = useState(true);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(false);
    const [search, setSearch] = useState('');

    const { data: strings } = useSelector((state: ApplicationState) => state.locale);
    const {
        data: { importDetails, importDetailsTotal },
        loading,
    } = useSelector((state: ApplicationState) => state.people);
    const token = getAuth();

    const dispatch = useDispatch();

    useEffect(() => {
        if (personId) {
            getPerson(personId);
        } else if (personId === 0) {
            // workaround for imports with personId === 0
            setPerson({ name: 'None', email: 'None' });
            setPersonLoading(false);
        }

        async function getPerson(id: number) {
            const { data: person } = await http.get(`${Constants.PEOPLE_URI}/${id}`);
            setPerson({ name: `${person.firstName} ${person.lastName}`, email: person.email });
            setPersonLoading(false);
        }
    }, [personId]);

    useEffect(() => {
        setPage(1);
    }, [limit]);

    useEffect(() => {
        if (id) {
            dispatch(getImportDetailRequest({ id, limit, offset: (page - 1) * limit, error, success, search }));
        }
    }, [id, limit, page, error, success, search]);

    const getErrorsURI = () => {
        return `${getHost()}/rest/person/imports/${id}/download?onlyErrors=true&token=${token}`;
    };

    return (
        <div
            aria-hidden="true"
            aria-labelledby="modalLabel"
            className="modal fade modal-import-history"
            data-backdrop="static"
            data-keyboard="false"
            id={target}
            role="dialog"
            tabIndex={-1}
        >
            <div className="modal-dialog modal-dialog-centered import-modal" role="document">
                <div className="alert-modal-content modal-content import-modal import-modal-content">
                    <div className="close-icon">
                        <i className="material-icons align-middle" data-dismiss="modal">
                            close
                        </i>
                    </div>
                    {personLoading && <Loader />}
                    {!personLoading && (
                        <>
                            <div className="modal-top-content">
                                <div className="name-email-container">
                                    <span className="span-name">{person.name}</span>
                                    <span className="span-email">{person.email}</span>
                                </div>
                                {importDetails && importDetails.length > 0 && (
                                    <>
                                        <div className="pagination-container">
                                            <span className="span-page">Pagina atual:</span>
                                            <Pagination
                                                activePage={page}
                                                itemClass="page-item"
                                                itemsCountPerPage={limit}
                                                linkClass="page-link"
                                                nextPageText={
                                                    <i className="material-icons align-middle" style={{ fontSize: 14 }}>
                                                        chevron_right
                                                    </i>
                                                }
                                                onChange={(val) => setPage(val)}
                                                pageRangeDisplayed={5}
                                                prevPageText={
                                                    <i className="material-icons align-middle" style={{ fontSize: 14 }}>
                                                        chevron_left
                                                    </i>
                                                }
                                                totalItemsCount={importDetailsTotal || 0}
                                            />
                                        </div>
                                        <div className="pagination-container">
                                            <span className="span-page">Itens por pagina:</span>
                                            <div className="dropdown">
                                                <button className="btn btn-primary btn-sm" data-toggle="dropdown">
                                                    {limit}
                                                    <i className="material-icons align-middle">arrow_drop_down</i>
                                                </button>
                                                <div aria-labelledby="dropdownMenuButton" className="dropdown-menu">
                                                    <span className="dropdown-item" onClick={() => setLimit(50)}>
                                                        50
                                                    </span>
                                                    <span className="dropdown-item" onClick={() => setLimit(30)}>
                                                        30
                                                    </span>
                                                    <span className="dropdown-item" onClick={() => setLimit(20)}>
                                                        20
                                                    </span>
                                                    <span className="dropdown-item" onClick={() => setLimit(10)}>
                                                        10
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="top-search-content modal-error-search">
                                {status !== 'COMPLETED' && (
                                    <>
                                        <button
                                            className="btn btn-primary btn-sm new-item-button status-button"
                                            data-toggle="dropdown"
                                            onClick={() => {}}
                                            type="button"
                                        >
                                            {strings.STATUS}
                                        </button>
                                        <div className="dropdown-menu dropdown-menu-right drop-three">
                                            <div className="triangle-container t-three">
                                                <div className="dropdown-triangle" />
                                                <div className="dropdown-triangle-border" />
                                            </div>
                                            <div
                                                className="dropdown-content"
                                                onClick={() => setSuccess((ps) => !ps)}
                                                onKeyDown={() => {}}
                                                role="button"
                                                tabIndex={0}
                                            >
                                                <i className="material-icons va-t m-right--xxs">
                                                    {success ? Constants.icons.CHECK_BOX : Constants.icons.CHECK_BOX_OUTLINE_BLANK}
                                                </i>
                                                <span>{strings.SUCCESS_WITH_EXCLAMATION}</span>
                                            </div>
                                            <div
                                                className="dropdown-content"
                                                onClick={() => setError((ps) => !ps)}
                                                onKeyDown={() => {}}
                                                role="button"
                                                tabIndex={0}
                                            >
                                                <i className="material-icons va-t m-right--xxs">
                                                    {error ? Constants.icons.CHECK_BOX : Constants.icons.CHECK_BOX_OUTLINE_BLANK}
                                                </i>
                                                <span>{strings.ERROR}</span>
                                            </div>
                                        </div>
                                    </>
                                )}
                                <div className="form-group inner-addon left-addon modal-error-search-input">
                                    <div>
                                        <i className="material-icons-outlined align-middle input-icon">search</i>
                                        <input
                                            className="form-control"
                                            onChange={(e) => setSearch(e.target.value)}
                                            placeholder={strings.SEARCH}
                                            type="search"
                                            value={search}
                                        />
                                    </div>
                                </div>
                                <a
                                    className="btn btn-primary btn-sm new-item-button download-button"
                                    href={getErrorsURI()}
                                    style={{ visibility: status === 'COMPLETED' ? 'hidden' : 'visible' }}
                                >
                                    {strings.DOWNLOAD_ERRORS}
                                </a>
                            </div>

                            <div style={{ overflow: 'auto' }}>
                                {loading && <Loader />}
                                {!loading && importDetails && importDetails.length > 0 && (
                                    <table className="table table-borderless table-sm table-striped">
                                        <thead>
                                            <tr>
                                                <th scope="col">{strings.EMAIL}</th>
                                                <th scope="col">{strings.STATUS}</th>
                                                <th scope="col" style={{ width: '40%' }}>
                                                    {strings.DETAILS}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {importDetails.map((id: ImportDetails) => (
                                                <tr className="table-row" key={id.id}>
                                                    <td scope="row">{id.email}</td>
                                                    <td>{strings[id.status]}</td>
                                                    <td>{id.unsuccessfulDetail ? id.unsuccessfulDetail : strings.CONCLUDED}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                            {importDetails && importDetails.length <= 0 && <p className="emptySection">{strings.EMPTY_IMPORT_DETAIL}</p>}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
export default Modal;
